import { useMutation, useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { LatLngHeightHPR } from '~/components/cesium/CesiumViewer';
import { CesiumViewer } from '~/components/cesium/CesiumViewer';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import type { Entity, ScreenSpaceEventHandler, Viewer } from 'cesium';
import { Cartographic, HeadingPitchRoll } from 'cesium';
import type { TilesetParams } from '~/components/cesium/cesiumUtils';
import {
  addIconPoint,
  addInfoBoxIconPoint,
  enableEntityInfoBox,
  flyToLocation,
  getCameraCartographic,
  removeEntity,
  TerrainProvider,
} from '~/components/cesium/cesiumUtils';
import { Button } from '~/components/ui/button';
import { useVftPageContext, VftPageContextProvider } from '~/utils/modules/vft';
import type { VftPageQueryQuery } from '~/apollo/generated/v4/graphql';
import { Heading } from '~/components/common/Heading';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ExpandedIcon } from '~/components/common/icons/ExpandedIcon';
import * as R from 'ramda';
import {
  iconDefaults,
  isIframeType,
  type SO,
} from '../model/outcrop/$outcropId/so-placements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPencil,
  faVideoCamera,
  faX,
} from '@fortawesome/free-solid-svg-icons';
import { cn, rejectNil } from '~/utils/common';
import { Menu } from '~/components/ui/menu';
import type { RefetchQueries } from '~/utils/graphql';
import { createRefetchQueries } from '~/utils/graphql';
import { NotFound } from '~/components/common/NotFound';
import { newVftViewerRoute } from '~/paths';
import { CreateVftSlideModal } from '~/components/upload/vft/create-slide-modal';
import { UpdateVftSlideModal } from '~/components/upload/vft/update-slide-modal';
import { gigaPanUrl } from '~/components/supportingObject/GigaPanViewer';

export const VFT_PAGE_QUERY = graphql(`
  query vftPageQuery($id: ID!) {
    virtualFieldTripGet(id: $id) {
      id
      lifecycleStatus
      name
      outcropTour
      outcrops {
        ...outcropVftParts
        virtualOutcropModels {
          ...virtualOutcropModelVftParts
        }
      }
      slides {
        ...tourSlideVftParts
      }
    }
  }
`);

const saveCameraToSlide = graphql(`
  mutation SaveCameraToSlide(
    $id: ID!
    $input: TourSlideSaveDefaultCameraInput!
  ) {
    tourSlideSaveDefaultCamera(id: $id, input: $input) {
      result {
        id
      }
    }
  }
`);

const deleteSlideMutation = graphql(`
  mutation deleteSlide($id: ID!) {
    tourSlideDelete(id: $id) {
      result {
        id
      }
    }
  }
`);

const saveTilesetToSlide = graphql(`
  mutation SaveTilesetToSlide($input: TourSlideAddCesiumAssetInput!) {
    tourSlideAddCesiumAsset(input: $input)
  }
`);

const removeTilesetFromSlide = graphql(`
  mutation RemoveTilesetFromSlide($input: TourSlideRemoveCesiumAssetInput!) {
    tourSlideRemoveCesiumAsset(input: $input)
  }
`);

const saveSoToSlide = graphql(`
  mutation SaveSoToSlide($input: TourSlideAddSupportingObjectInput!) {
    tourSlideAddSupportingObject(input: $input)
  }
`);

const removeSoFromSlide = graphql(`
  mutation RemoveSoFromSlide($input: TourSlideRemoveSupportingObjectInput!) {
    tourSlideRemoveSupportingObject(input: $input)
  }
`);

export type VftType = VftPageQueryQuery['virtualFieldTripGet'];
export type OutcropType = Pick<
  NonNullable<VftType>,
  'outcrops'
>['outcrops'][number];
export type VomType = OutcropType['virtualOutcropModels'][number];
export type StudyType = OutcropType['studies'][number];
export type SlideType = Pick<NonNullable<VftType>, 'slides'>['slides'][number];
export type SlidePlacementType = SlideType['placements'][number];
export type CAType = NonNullable<VomType['cesiumAsset']>;

export function placementToSO(placement: SlidePlacementType): SO | null {
  const potentialType =
    placement.crossSection ||
    placement.faciesSchema ||
    placement.gigaPan ||
    placement.miniModel ||
    placement.photo360 ||
    placement.picture ||
    placement.production ||
    placement.reservoirModel ||
    placement.sedimentaryLog ||
    placement.trainingImage ||
    placement.variogram ||
    placement.video ||
    placement.wellLog;
  if (placement.fieldPicture) {
    return {
      ...placement.fieldPicture,
      id: placement.fieldPicture.id,
      name:
        placement.fieldPicture.description ??
        `Field Picture ${placement.fieldPicture.id}`,
    };
  }
  return potentialType || null;
}

export const addEntity =
  (viewer: Viewer, show: boolean = true, showLabel: boolean = true) =>
  (item: SO): Entity | null => {
    if (item.placement?.location) {
      const position = {
        latitude: item.placement.location.latitude,
        longitude: item.placement.location.longitude,
        height: item.placement.location.height,
        heading: item.placement.location.heading,
        pitch: item.placement.location.pitch,
        roll: item.placement.location.roll,
      } satisfies LatLngHeightHPR;
      let picUrl = item.file?.signedUrl;
      if (!picUrl && item.pictures) {
        picUrl = item.pictures.at(0)?.file.signedUrl;
      }
      const itemId = `${item.__typename}-${item.id}`;
      if (viewer.entities.getById(itemId) === undefined && item.placement) {
        if (item.__typename && isIframeType(item) && (item.url || item.gigaPanHash)) {
          const iframeUrl = item.url || gigaPanUrl(item.gigaPanHash || '', 0);
          return addInfoBoxIconPoint({
            viewer,
            id: itemId,
            panelUrl: iframeUrl,
            position,
            label: showLabel ? item.placement?.displayName || item.name : '',
            iframe: true,
            show,
            iconName: item.placement.icon || iconDefaults[item.__typename],
            width: item.placement.panelWidth ?? undefined,
            height: item.placement.panelHeight ?? undefined,
          });
        } else if (picUrl && item.__typename) {
          return addInfoBoxIconPoint({
            viewer,
            id: itemId,
            panelUrl: picUrl,
            position,
            label: showLabel ? item.placement?.displayName || item.name : '',
            iframe: false,
            show,
            iconName: item.placement.icon || iconDefaults[item.__typename],
            width: item.placement.panelWidth ?? undefined,
            height: item.placement.panelHeight ?? undefined,
          });
        } else {
          return addIconPoint({
            viewer,
            id: itemId,
            icon:
              item.placement.icon || iconDefaults[item.__typename || 'Marker'],
            position,
            show,
            label: showLabel ? item.placement?.displayName || item.name : '',
          });
        }
      }
    }
    return null;
  };

export default function VftPage() {
  const params = useParams();
  invariant(params.vftId, 'vomId param required');
  const vftId = parseInt(params.vftId);
  useBreadcrumb('routes/upload/vft-manager', 'VFT Manager');

  return (
    <ApolloProviderV4>
      <VftPageContextProvider>
        <VftDataFetch id={vftId} />
      </VftPageContextProvider>
    </ApolloProviderV4>
  );
}

function VftDataFetch({ id }: { id: number }) {
  const { data, loading, refetch } = useQuery(VFT_PAGE_QUERY, {
    variables: {
      id: String(id),
    },
  });

  if (loading) {
    return <SpinnerPlaceholder />;
  }
  const vft = data?.virtualFieldTripGet;

  if (!vft) {
    return <NotFound />;
  }

  return <InnerPage vft={vft} />;
}

function SlideSidebar({
  slides,
  vftId,
}: {
  slides: SlideType[];
  vftId: string;
}) {
  const { viewer, setCurrentSlideId } = useVftPageContext();
  const sortedSlides = R.sortBy(s => s.position, slides);

  return (
    <div className="space-y-4">
      <Heading level={2}>Slides</Heading>
      <CreateVftSlideModal
        vftId={Number(vftId)}
        viewer={viewer}
        successCallback={(id: string) => {
          setCurrentSlideId(id);
        }}
      >
        {showModal => (
          <Button
            type="button"
            color="primary"
            size="sm"
            onClick={showModal}
            className="ml-2"
          >
            Create Slide
          </Button>
        )}
      </CreateVftSlideModal>
      <div className="space-y-1">
        <Menu>
          {sortedSlides.map(slide => {
            return (
              <SlideItem
                key={`slide-${slide.id}`}
                slide={slide}
                vftId={vftId}
              />
            );
          })}
        </Menu>
      </div>
    </div>
  );
}

function SlideItem({ slide, vftId }: { slide: SlideType; vftId: string }) {
  const { viewer, currentSlideId, setCurrentSlideId } = useVftPageContext();
  const [slideDelete, { loading: loadingSlideDelete }] = useMutation(
    deleteSlideMutation,
    {},
  );
  function setSlide(slide: SlideType) {
    // set view to slide default camera
    // set viwer to default camera
    setCurrentSlideId(slide.id);
    if (viewer && slide.defaultCamera) {
      flyToLocation(viewer, slide.defaultCamera);
    }
  }

  async function deleteSlide(id: string, vftId: string) {
    const refetchQueries = createRefetchQueries([
      {
        query: VFT_PAGE_QUERY,
        variables: { id: vftId },
      },
    ]);
    const res = await slideDelete({
      variables: {
        id,
      },
      refetchQueries,
    });
    return res;
  }

  return (
    <>
      <div
        onClick={() => {
          setSlide(slide);
        }}
        key={slide.id}
        className={cn('cursor-pointer', {
          'text-slate-800 font-bold': slide.id === currentSlideId,
          'text-slate-800': slide.id !== currentSlideId,
        })}
      >
        {slide.position} - {slide.title}
        <div className="text-right float-right">
          <UpdateVftSlideModal
            successCallback={(id: string) => {
              setCurrentSlideId(id);
            }}
            vftSlide={slide}
          >
            {showModal => (
              <Button
                type="button"
                color="ghost"
                size="xs"
                onClick={showModal}
                className="ml-2"
              >
                <FontAwesomeIcon icon={faPencil} />
              </Button>
            )}
          </UpdateVftSlideModal>
          <Button
            type="button"
            color="ghost"
            size="xs"
            disabled={loadingSlideDelete}
            onClick={() => {
              deleteSlide(slide.id, vftId);
            }}
          >
            <FontAwesomeIcon icon={faX} />
          </Button>
        </div>
      </div>
    </>
  );
}

function DataSidebar({
  vft,
  initialTilesets,
  initialCesiumAssets,
}: {
  vft: VftType;
  initialTilesets: TilesetParams[];
  initialCesiumAssets: CAType[];
}) {
  const outcrops = vft?.outcrops || [];
  const refetchQueries = vft
    ? createRefetchQueries([
        {
          query: VFT_PAGE_QUERY,
          variables: { id: vft.id },
        },
      ])
    : createRefetchQueries([]);
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        {!!outcrops.length && (
          <ExpandableSection heading="Outcrops">
            <div className="space-y-1">
              {outcrops.map(outcrop => (
                <OutcropItem
                  key={`outcrop-${outcrop.id}`}
                  outcrop={outcrop}
                  initialTilesets={initialTilesets}
                  initialCesiumAssets={initialCesiumAssets}
                  refetchQueries={refetchQueries}
                />
              ))}
            </div>
          </ExpandableSection>
        )}
      </div>
    </div>
  );
}

export const filterByOutcropTag = (outcropId: number) => (so: SO) =>
  so.outcropTagId === outcropId;

export const studyHasPlaceableContent = (outcropId: number) => (study: StudyType) => {
  const soFilter = filterByOutcropTag(outcropId);
  const hasPicture = (so: SO) => (so.pictures?.length ?? 0) > 0;

  const picture = study.pictures.find(soFilter);
  if (picture) return true;
  const cs = study.crossSections.filter(soFilter).find(hasPicture);
  if (cs) return true;
  const facies = study.facies.filter(soFilter).find(hasPicture);
  if (facies) return true;
  const sl = study.sedimentaryLogs.filter(soFilter).find(hasPicture);
  if (sl) return true;
  const wl = study.wellLogs.filter(soFilter).find(hasPicture);
  if (wl) return true;
  const p = study.production.filter(soFilter).find(hasPicture);
  if (p) return true;
  const rm = study.reservoirModels.filter(soFilter).find(hasPicture);
  if (rm) return true;
  const ti = study.trainingImages.filter(soFilter).find(hasPicture);
  if (ti) return true;
  const v = study.variograms.filter(soFilter).find(hasPicture);
  if (v) return true;
  const gp = study.gigaPans.filter(soFilter).find(hasPicture);
  if (gp) return true;

  return false;
};

export function VomItem({
  vom,
  initialTilesets,
  initialCesiumAssets,
  refetchQueries,
}: {
  vom: VomType;
  initialTilesets: TilesetParams[];
  initialCesiumAssets: CAType[];
  refetchQueries?: RefetchQueries;
}) {
  const { viewer, slides, currentSlideId, hiddenTilesets, setHiddenTilesets } =
    useVftPageContext();
  const [saveSlideTileset, { loading: loadingSaveSlideTileset }] = useMutation(
    saveTilesetToSlide,
    {},
  );
  const [removeSlideTileset, { loading: loadingRemoveSlideTileset }] =
    useMutation(removeTilesetFromSlide, {});
  const tsAsset = vom.cesiumAsset;
  const token = tsAsset?.tilesetToken?.token;
  const camera = tsAsset?.defaultCamera;
  let initialState = false;
  const [isVisible, setIsVisible] = useState(initialState);

  useEffect(() => {
    if (currentSlideId && token && slides.length > 0) {
      const currentSlide = slides.find(s => s.id === currentSlideId);
      if (currentSlide) {
        const found = currentSlide.cesiumAssets.find(
          ca => ca?.id === vom.cesiumAsset?.id,
        );
        setIsVisible(!!found);
      }
    }
  }, [currentSlideId]);

  function handleFlyTo() {
    if (!camera || !viewer) return;
    flyToLocation(viewer, camera);
  }

  function handleToggleVisibility(token: string | undefined) {
    const isVisibleNow = !isVisible;
    const ts = initialTilesets.find(ts => {
      return ts.assetToken === token;
    });
    if (!ts || !token) {
      return;
    }
    if (isVisibleNow) {
      setHiddenTilesets(hiddenTilesets.filter(ts => ts.assetToken !== token));
    } else {
      setHiddenTilesets([...hiddenTilesets, ts]);
    }
    setIsVisible(isVisibleNow);
    toggleSaveTileset(isVisibleNow, token);
  }

  function toggleSaveTileset(save: boolean, token: string | undefined) {
    const ca = initialCesiumAssets.find(ca => ca?.id === vom.cesiumAsset?.id);
    if (!ca || !currentSlideId) {
      return;
    }
    const input = {
      cesiumAssetId: Number(ca.id),
      slideId: Number(currentSlideId),
    };
    if (save) {
      saveSlideTileset({
        variables: {
          input,
        },
        refetchQueries,
      });
    } else {
      removeSlideTileset({
        variables: {
          input,
        },
        refetchQueries,
      });
    }
  }

  return (
    <div className="flex justify-between gap-6 w-full items-center">
      <div className="text-left flex">
        <input
          type="checkbox"
          className="checkbox checkbox-xs"
          checked={!!token && isVisible}
          onChange={() => {
            handleToggleVisibility(token);
          }}
          disabled={
            !token || loadingSaveSlideTileset || loadingRemoveSlideTileset
          }
        />
        <div
          className={cn(
            'self-center ml-2 pl-2 border-l border-slate-200 border-dotted text-base max-w-full leading-4',
            {
              'text-slate-800': !!token,
              'text-slate-400': !token,
            },
          )}
        >
          {vom.name}
        </div>
      </div>

      <div className="text-right">
        {token && camera && (
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={handleFlyTo}
            disabled={!token}
          >
            <FontAwesomeIcon icon={faVideoCamera} />
          </Button>
        )}
      </div>
    </div>
  );
}

function SOItem({
  item,
  refetchQueries,
}: {
  item: SO;
  refetchQueries?: RefetchQueries;
}) {
  const { viewer, currentSlideId, slides, showLabels } = useVftPageContext();
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const [addSlideSo, { loading: loadingAddSlideSo }] = useMutation(
    saveSoToSlide,
    {},
  );
  const [removeSlideSo, { loading: loadingRemoveSlideSo }] = useMutation(
    removeSoFromSlide,
    {},
  );

  const hasPlacement = !!item.placement;
  const soId = `${item.__typename}-${item.id}`;
  const camera = item.placement?.defaultCamera

  useEffect(() => {
    if (currentSlideId && slides.length > 0) {
      const currentSlide = slides.find(s => s.id === currentSlideId);
      if (currentSlide && hasPlacement) {
        const found = currentSlide.placements.find(
          p => p.id === item.placement?.id,
        );
        setIsVisible(!!found);
        if (viewer) {
          if (found) {
            addEntity(viewer, true, showLabels)(item);
          } else {
            removeEntity(viewer, soId);
          }
        }
      }
    }
  }, [currentSlideId]);

  function handleFlyTo() {
    if (!camera || !viewer) return;
    flyToLocation(viewer, camera);
  }


  function handleToggleVisibility() {
    const newVisible = !isVisible;
    setIsVisible(newVisible);
    if (viewer) {
      if (newVisible) {
        addEntity(viewer, true, showLabels)(item);
      } else {
        removeEntity(viewer, soId);
      }
    }
    if (refetchQueries && refetchQueries.length > 0) {
      toggleSaveSo(newVisible);
    }
  }

  function toggleSaveSo(save: boolean) {
    if (currentSlideId && item.placement) {
      const input = {
        supportingObjectPlacementId: Number(item.placement.id),
        slideId: Number(currentSlideId),
      };
      if (save) {
        addSlideSo({
          variables: {
            input,
          },
          refetchQueries: refetchQueries || [],
        });
      } else {
        removeSlideSo({
          variables: {
            input,
          },
          refetchQueries: refetchQueries || [],
        });
      }
    }
  }

  return (
    <div className="flex justify-between gap-6 w-full items-center">
      <div
        className="flex justify-between gap-2 w-full items-start"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input
          type="checkbox"
          className="checkbox checkbox-xs"
          checked={!!hasPlacement && isVisible}
          onChange={handleToggleVisibility}
          disabled={!hasPlacement || loadingAddSlideSo || loadingRemoveSlideSo}
        />
        <label
          className={cn(
            'label items-start border-l-2 border-slate-200 border-dotted grow pt-1 pl-2.5',
            {
              truncate: !isHovered,
            },
          )}
        >
          <div
            className={cn('w-full', {
              truncate: !isHovered,
            })}
          >
            {item.placement?.displayName ?? item.name}
          </div>
        </label>
        <div className="text-right">
        {camera && (
          <Button
            type="button"
            color="ghost"
            size="xs"
            onClick={handleFlyTo}
            disabled={!camera}
          >
            <FontAwesomeIcon icon={faVideoCamera} />
          </Button>
        )}
      </div>
      </div>
    </div>
  );
}

function SOSection({
  label,
  items,
  refetchQueries,
  removeSopIds = []
}: {
  label: string;
  items: SO[];
  refetchQueries?: RefetchQueries;
  removeSopIds?: string[];
}) {
  if (!items.length) return null;

  const sortedItems = R.sortBy(item => item.name.toLowerCase(), items).filter(
    item => {
      return !!item.placement;
    },
  ).filter(item => !!item.placement && !removeSopIds.includes(item.placement.id));

  if (!sortedItems.length) {
    return null;
  }

  return (
    <div>
      <div className="text-xs text-muted">{label}</div>
      <div className="space-y-1">
        {sortedItems.map(item => (
          <SOItem key={item.id} item={item} refetchQueries={refetchQueries} />
        ))}
      </div>
    </div>
  );
}

export function OutcropSection({
  outcrop,
  refetchQueries,
  removeSopIds
}: {
  outcrop: OutcropType;
  refetchQueries?: RefetchQueries;
  removeSopIds?: string[]
}) {
  return (
    <div className="border border-slate-100 p-2">
      <Heading level={4} className="sticky top-0 mt-0 bg-white">
        {outcrop.name}
      </Heading>
      <div className="space-y-2">
        <SOSection
          label="Pictures"
          items={outcrop.pictures}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Cross Sections"
          items={outcrop.crossSections}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Facies"
          items={outcrop.facies}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Sedimentary Logs"
          items={outcrop.sedimentaryLogs}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Well Logs"
          items={outcrop.wellLogs}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Production"
          items={outcrop.production}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Reservoir Models"
          items={outcrop.reservoirModels}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Training Images"
          items={outcrop.trainingImages}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Variograms"
          items={outcrop.variograms}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Giga Pans"
          items={outcrop.gigaPans}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Field Pictures"
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
          items={outcrop.fieldPictures.map(fp => ({
            ...fp,
            id: fp.id,
            name: fp.file.name ?? `Field Picture ${fp.id}`,
          }))}
        />
        <SOSection
          label="Mini-Models"
          items={outcrop.miniModels}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="360 Photos"
          items={outcrop.photo360s}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
        <SOSection
          label="Videos"
          items={outcrop.videos}
          refetchQueries={refetchQueries}
          removeSopIds={removeSopIds}
        />
      </div>
    </div>
  );
}

export function OutcropItem({
  outcrop,
  initialTilesets,
  initialCesiumAssets,
  refetchQueries,
}: {
  outcrop: OutcropType;
  initialTilesets: TilesetParams[];
  initialCesiumAssets: CAType[];
  refetchQueries?: RefetchQueries;
}) {
  const outcropId = parseInt(outcrop.id);
  const studies = R.pipe(
    () => outcrop.studies,
    R.uniqBy(study => study.id),
    R.sortBy(study => study.name ?? ''),
    R.filter(studyHasPlaceableContent(outcropId)),
  )();

  const voms = outcrop.virtualOutcropModels || [];

  return (
    <div className="space-y-4">
      {!!voms.length && (
        <ExpandableSection heading={outcrop.name}>
          <div className="space-y-1">
            {voms.map(vom => (
              <VomItem
                key={`vom-${vom.id}`}
                vom={vom}
                initialTilesets={initialTilesets}
                initialCesiumAssets={initialCesiumAssets}
                refetchQueries={refetchQueries}
              />
            ))}
          </div>
        </ExpandableSection>
      )}

      <OutcropSection outcrop={outcrop} refetchQueries={refetchQueries} />

      {!!studies.length && (
        <ExpandableSection heading="Studies">
          <div className="space-y-2">
            {studies.map(study => (
              <StudySection
                key={study.id}
                outcropId={outcropId}
                study={study}
                refetchQueries={refetchQueries}
              />
            ))}
          </div>
        </ExpandableSection>
      )}
    </div>
  );
}

export function StudySection({
  outcropId,
  study,
  refetchQueries,
  removeSopIds
}: {
  outcropId: number;
  study: StudyType;
  refetchQueries?: RefetchQueries;
  removeSopIds?: string[];
}) {
  const soFilter = filterByOutcropTag(outcropId);

  return (
    <div className="border border-slate-100 p-2">
      <ExpandableSection
        heading={
          <div className="w-full">
            <Heading
              level={4}
              className="text-black sticky top-0 bg-white truncate max-w-full"
            >
              {study.name}
            </Heading>
            <div className="text-sm">
              {[study.collectedYear, study.collectedBy]
                .filter(rejectNil)
                .join(' - ')}
            </div>
          </div>
        }
      >
        <div className="space-y-2">
          <SOSection
            label="Pictures"
            items={study.pictures.filter(soFilter)}
            refetchQueries={refetchQueries}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Cross Sections"
            refetchQueries={refetchQueries}
            items={study.crossSections.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Facies"
            items={study.facies.filter(soFilter)}
            refetchQueries={refetchQueries}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Sedimentary Logs"
            refetchQueries={refetchQueries}
            items={study.sedimentaryLogs.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Well Logs"
            refetchQueries={refetchQueries}
            items={study.wellLogs.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Production"
            refetchQueries={refetchQueries}
            items={study.production.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Reservoir Models"
            refetchQueries={refetchQueries}
            items={study.reservoirModels.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Training Images"
            refetchQueries={refetchQueries}
            items={study.trainingImages.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Variograms"
            refetchQueries={refetchQueries}
            items={study.variograms.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
          <SOSection
            label="Giga Pans"
            refetchQueries={refetchQueries}
            items={study.gigaPans.filter(soFilter)}
            removeSopIds={removeSopIds}
          />
        </div>
      </ExpandableSection>
    </div>
  );
}

export function ExpandableSection({
  heading,
  children,
}: {
  heading: ReactNode;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex justify-between items-center pr-2"
      >
        <div className="grow shrink-0 text-left max-w-full px-2">
          {typeof heading === 'string' ? (
            <Heading level={3}>{heading}</Heading>
          ) : (
            heading
          )}
        </div>
        <div className="shrink-0 ">
          <ExpandedIcon expanded={expanded} />
        </div>
      </button>

      {expanded && children}
    </div>
  );
}

function InnerPage({ vft }: { vft: NonNullable<VftType> }) {
  const {
    viewer,
    setViewer,
    slides,
    setSlides,
    currentSlideId,
    setCurrentSlideId,
    hiddenTilesets,
    setHiddenTilesets,
  } = useVftPageContext();
  const [saveSlideCamera, { loading: loadingSaveCamera }] = useMutation(
    saveCameraToSlide,
    {},
  );
  const [infoBoxHandler, setInfoBoxHandler] =
    useState<ScreenSpaceEventHandler>();
  const refetchQueries = createRefetchQueries([
    {
      query: VFT_PAGE_QUERY,
      variables: { id: vft.id },
    },
  ]);
  const outcrops = useMemo(() => {
    return vft?.outcrops || [];
  }, [vft]);
  const initialSlides = useMemo(() => {
    return vft?.slides || [];
  }, [vft]);

  useEffect(() => {
    setSlides(initialSlides);
  }, [initialSlides, setSlides]);

  const initialTilesets = useMemo(() => {
    const its: TilesetParams[] = [];
    outcrops?.forEach(oc => {
      oc.virtualOutcropModels.forEach(vom => {
        const cur = vom.cesiumAsset;
        if (
          !cur ||
          !cur.location ||
          !cur.tilesetToken ||
          !cur.tilesetUrlLocal
        ) {
          return;
        }
        its.push({
          transform: {
            location: Cartographic.fromDegrees(
              cur.location.longitude,
              cur.location.latitude,
              cur.location.height,
            ),
            orientation: new HeadingPitchRoll(
              cur.location.heading,
              cur.location.pitch,
              cur.location.roll,
            ),
          },
          assetToken: cur.tilesetToken.token,
          localPath: cur.tilesetUrlLocal,
        } satisfies TilesetParams);
      });
    });
    return its;
  }, [outcrops]);

  const initialCesiumAssets = useMemo(() => {
    const initialCesiumAssets: CAType[] = [];
    outcrops?.forEach(oc => {
      oc.virtualOutcropModels.forEach(vom => {
        const cur = vom.cesiumAsset;
        if (
          !cur ||
          !cur.location ||
          !cur.tilesetToken ||
          !cur.tilesetUrlLocal
        ) {
          return;
        }
        initialCesiumAssets.push(cur);
      });
    });
    return initialCesiumAssets;
  }, [outcrops]);

  useEffect(() => {
    // slide changed
    const currentSlide = slides.find(s => s.id === currentSlideId);
    if (!currentSlide) {
      return;
    }
    const slideCAUrls = currentSlide.cesiumAssets.map(
      ca => ca?.tilesetUrlLocal,
    );
    const newHidden = [...initialTilesets].filter(
      ts => !slideCAUrls.includes(ts.localPath),
    );
    setHiddenTilesets(newHidden);
    if (viewer) {
      viewer.entities.removeAll();
      currentSlide.placements.forEach(p => {
        const so = placementToSO(p);
        if (so) {
          addEntity(viewer, true)(so);
        }
      });
    }
  }, [currentSlideId, initialTilesets, slides]);

  function saveCamera() {
    const camera = getCameraCartographic(viewer);
    const slide = slides.find(s => {
      return s.id === currentSlideId;
    });
    if (slide && camera) {
      saveSlideCamera({
        variables: {
          id: Number(slide.id),
          input: {
            location: camera,
          },
        },
        refetchQueries: refetchQueries,
      });
    }
  }

  function hideInitialTilesets() {
    setHiddenTilesets(initialTilesets);
  }

  useEffect(() => {
    if (infoBoxHandler) {
      infoBoxHandler.destroy();
    }
    if (viewer) {
      const handler = enableEntityInfoBox(viewer, entity => {
        console.log('entity', entity);
      });
      setInfoBoxHandler(handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  return (
    <>
      <div className="grid lg:grid-cols-6 gap-6">
        <div className="max-h-screen overflow-auto">
          <SlideSidebar slides={initialSlides} vftId={vft.id} />
        </div>
        <div className="max-h-screen overflow-auto">
          <DataSidebar
            vft={vft}
            initialTilesets={initialTilesets}
            initialCesiumAssets={initialCesiumAssets}
          />
        </div>

        <div className="lg:col-span-4">
          <div className="float-right my-1">
            <Link to={newVftViewerRoute(vft.id)} className="link">
              Viewer Page
            </Link>
          </div>

          <CesiumViewer
            initialTilesets={initialTilesets}
            showGlobe={true}
            terrainProvider={TerrainProvider.World}
            hideTilesets={hiddenTilesets}
            defaultZoom={false}
            afterLoad={hideInitialTilesets}
            infoBox={true}
            // cameraCallback={}
            sendCesiumViewer={setViewer}
          />
          {currentSlideId && (
            <>
              <Button disabled={loadingSaveCamera} onClick={saveCamera}>
                Update Camera for Slide
              </Button>
              <form>
                <input></input>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
